import Section, { Reveal } from '../components/Section';
import ContactForm from '../components/ContactForm';
import { LOGOS } from '../cdn';

const TIMELINE = [
  { year: '2020', title: 'TNIDU founded',          body: 'TNIDU Inc. established in Colombo. Early R&D on censorship-resistant tunnels and privacy tooling.' },
  { year: '2022', title: 'Prototype',              body: 'First stealth-tunnel proof-of-concept on a single Colombo server. Two testers, one debugger, three all-nighters.' },
  { year: '2023', title: 'Public beta',            body: 'Android app launches on the Play Store. Testers in Sri Lanka + Iran hit five-nines uptime. First Pro plans sold.' },
  { year: '2025', title: 'v2 API + hardening',     body: 'HMAC-signed JSON API, post-quantum encryption shipped, auto-repair sweep, admin panel rewrite.' },
  { year: '2026', title: 'Cross-platform',         body: 'Windows Electron client, Capacitor rebuild for Android, marketing site + user portal, Telegram support bot.' },
  { year: 'Next',  title: 'iOS + fleet expansion', body: 'iOS via TestFlight, second server region (SGP), Alipay / WeChat payments, in-app coupon marketplace.' },
];

const VALUES = [
  { title: 'Privacy is a right',        body: 'Not a premium feature. Not something to opt into. On by default, unconditional — including on the Free tier.' },
  { title: 'No dark patterns',          body: "Free tier is Free forever. Auto-renew is off by default. Refunds are honored fairly — no support-runaround required." },
  { title: 'Show the work',             body: 'The admin panel exposes exactly what the backend thinks your plan is. No black boxes, no plausible-deniability opacity.' },
  { title: 'Own the stack',             body: "We wrote the client, the panel, the API — and integrated the tunnel end-to-end. When something breaks at 3 AM it's a code problem, not a vendor ticket." },
  { title: 'Ship boring reliability',   body: 'Watchdog probes every 15 s. If the tunnel dies, it comes back before you notice. Auto-repair before auto-blame.' },
  { title: 'Answer humans',             body: 'The Telegram support bot forwards every user message to a human within minutes. No triage tiers. No hold music.' },
];

const STATS = [
  { n: '99.4%', l: 'Handshake success' },
  { n: '48K+',  l: 'Sessions this month' },
  { n: '17',    l: 'Countries with active users' },
  { n: '<15s',  l: 'Time to first byte' },
];

const STACK = [
  'Open-source stealth tunnel core',
  'Post-quantum key exchange',
  'Native TUN bridge',
  'PHP 8.5 · SQLite (WAL)',
  'Capacitor 6 · Android SDK 34',
  'Electron 41 · Windows native proxy',
  'React 18 · Vite 5 · Tailwind 4',
  'MinIO / S3 (TNIDU Hosting Services)',
  'Telegram Bot API (support bridge)',
];

export default function About() {
  return (
    <>
      {/* HERO — TNIDU parent brand mark. `logoWhite` filter recolors the brand
          asset without needing a second CDN upload; mix-blend keeps it crisp
          on the ambient orb backdrop. */}
      <Section className="!pt-24 !pb-14">
        <div className="relative">
          <div aria-hidden className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-[color:var(--color-brand)]/18 blur-[120px]" />
          <Reveal>
            <div className="relative flex flex-col items-center text-center">
              <img
                src={LOGOS.tnidu}
                alt="TNIDU Inc."
                width={96}
                height={96}
                className="w-24 h-24 object-contain"
                style={{ filter: 'brightness(0) invert(1)' }}
                loading="eager"
                decoding="async"
              />
              <div className="chip mt-6">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--color-brand-glow)] animate-pulse" />
                Since 2020 · Colombo, Sri Lanka
              </div>
              <h1 className="h-title mt-4 text-5xl sm:text-6xl font-extrabold leading-[1.02] max-w-3xl">
                Small team.{' '}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[color:var(--color-brand)] via-[color:var(--color-brand-glow)] to-[color:var(--color-brand-deep)]">
                  Uncompromising tunnel.
                </span>
              </h1>
              <p className="mt-5 max-w-2xl text-lg text-[color:var(--color-ink-dim)] leading-relaxed">
                We started TNIDU Inc. because every commercial VPN we paid for eventually got fingerprinted, throttled, or plainly blocked. Vpnee is our answer — an in-house stealth tunnel that survives real censorship, backed by an admin panel that shows the truth.
              </p>
            </div>
          </Reveal>

          {/* Stats strip */}
          <Reveal delay={0.1}>
            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {STATS.map(s => (
                <div key={s.l} className="glass p-5 text-center">
                  <div className="h-title text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-[color:var(--color-brand)] via-[color:var(--color-brand-glow)] to-[color:var(--color-brand-deep)]">{s.n}</div>
                  <div className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)] mt-2">{s.l}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </Section>

      {/* MISSION */}
      <Section chip="Mission" title="Ship the tunnel we wanted to buy.">
        <div className="grid gap-6 lg:grid-cols-[1.15fr_1fr] items-start">
          <Reveal>
            <div className="glass p-8">
              <p className="text-[color:var(--color-ink-dim)] leading-relaxed">
                Most commercial VPNs sell the same repackaged WireGuard tunnel. Great throughput, awful evasion — one nation-state DPI update and you're offline for a week. Vpnee wraps our stealth transport inside a post-quantum handshake so your traffic looks identical to a normal TLS 1.3 conversation with a real CDN. If a censor can block us, they can block the CDN too. That's a fight we're happy to have.
              </p>
              <p className="mt-4 text-[color:var(--color-ink-dim)] leading-relaxed">
                Everything TNIDU ships is opinionated by design. No fake military-grade badges, no unread policy PDFs, no telemetry we don't tell you about. When we don't know the answer to a support question, we say so.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="rounded-2xl overflow-hidden border border-[color:var(--color-brand)]/25 bg-[color:var(--color-surface)] p-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-xl grid place-items-center overflow-hidden ring-1 ring-[color:var(--color-brand)]/30 bg-[color:var(--color-bg)]">
                <img src={LOGOS.vpnee} alt="Vpnee" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="mt-4 text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">Flagship product</div>
              <div className="h-title text-2xl font-extrabold mt-1">Vpnee</div>
              <p className="mt-3 text-sm text-[color:var(--color-ink-dim)] leading-relaxed">
                Post-quantum, zero-log, stealth-cloaked VPN. Android, Windows, and a browser portal — one signed-in identity across the fleet.
              </p>
              <div className="mt-5 text-xs text-[color:var(--color-ink-faint)]">
                <span className="inline-block px-2 py-1 rounded-md bg-white/5 border border-white/10">by TNIDU Inc.</span>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* VALUES */}
      <Section chip="Values" title="What we won't ship.">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.04}>
              <div className="glass p-6 h-full">
                <div className="w-10 h-10 rounded-xl grid place-items-center bg-[color:var(--color-brand)]/12 border border-[color:var(--color-brand)]/25 text-[color:var(--color-brand-glow)] mb-4">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
                <h3 className="h-title font-bold text-lg mb-2">{v.title}</h3>
                <p className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* TIMELINE */}
      <Section chip="Timeline" title="Where we've been. Where we're going.">
        <ol className="relative border-l border-[color:var(--color-line-2)] pl-6 space-y-8">
          {TIMELINE.map((t, i) => (
            <Reveal key={t.year} delay={i * 0.05}>
              <li className="relative">
                <span className={`absolute -left-[31px] top-1 w-4 h-4 rounded-full ring-4 ring-[color:var(--color-bg)] ${t.year === 'Next' ? 'bg-[color:var(--color-ink-faint)]' : 'bg-gradient-to-br from-[color:var(--color-brand)] to-[color:var(--color-brand-deep)]'}`} />
                <div className={`text-xs uppercase tracking-widest font-semibold mb-1 ${t.year === 'Next' ? 'text-[color:var(--color-ink-faint)]' : 'text-[color:var(--color-brand-glow)]'}`}>{t.year}</div>
                <div className="h-title font-bold text-xl mb-1">{t.title}</div>
                <p className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed max-w-2xl">{t.body}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </Section>

      {/* STACK */}
      <Section chip="Stack" title="Boring where it matters.">
        <Reveal>
          <div className="glass p-8">
            <p className="text-[color:var(--color-ink-dim)] leading-relaxed max-w-3xl">
              Nothing exotic. No custom protocols — proven stealth transport does the heavy lifting. Everything below runs on a $20 Colombo VPS and a MinIO cluster. If you can debug it with <span className="font-mono">strace</span> and a coffee, we like it.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {STACK.map(s => (
                <span key={s} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs font-mono text-[color:var(--color-ink-dim)]">
                  {s}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </Section>

      {/* CONTACT */}
      <Section chip="Contact" title="Say hi. We reply.">
        <div className="grid gap-5 md:grid-cols-3">
          {[
            { l: 'General',   e: 'hello@tnidu.com',   d: 'Feature requests, partnership pitches, philosophical debates about DPI.' },
            { l: 'Billing',   e: 'billing@tnidu.com', d: 'Invoices, refunds, coupon codes. Alipay / WeChat manual QR requests too.' },
            { l: 'Privacy',   e: 'privacy@tnidu.com', d: 'Data-subject requests, deletion, GDPR / PDPA compliance queries.' },
          ].map(c => (
            <Reveal key={c.e}>
              <a href={`mailto:${c.e}`} className="block glass p-6 h-full transition hover:border-[color:var(--color-brand)]/40 group">
                <div className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">{c.l}</div>
                <div className="mt-1 font-mono text-[color:var(--color-brand-glow)] group-hover:underline">{c.e}</div>
                <p className="mt-3 text-sm text-[color:var(--color-ink-dim)] leading-relaxed">{c.d}</p>
              </a>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-10">
            <ContactForm />
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-10 text-center text-sm text-[color:var(--color-ink-faint)] flex items-center justify-center gap-3">
            <img src={LOGOS.tnidu} alt="" width={22} height={22} className="opacity-70" style={{ filter: 'brightness(0) invert(1)' }} />
            TNIDU Inc. · Colombo, Sri Lanka · Est. 2020
          </div>
        </Reveal>
      </Section>
    </>
  );
}
